
import { FormControl, InputLabel, MenuItem, Paper, Select, SelectChangeEvent, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { useMemo, useState } from "react";
import { TOpcoesVisualizacao, coresTabelas, mostrarDadoPrimario } from "../../../..";
import moment, { Moment } from "moment";
import { useQuery } from "react-query";
import axiosClient from "../../../../../../lib/axios";
import LoadingApiQuery from "../../../../../../components/LoadingApiQuery";
import { formatarValorMonetario } from "../../../../../../helpers/masks";
import { IDataResumoTitulosReceber } from "../../../../../../types/api/financeiro/resumo-titulos-receber";
import { TPreFilters } from "../Listagem";

export default function TableTitulosReceber({
    handleChangePreFilters,
    preVisualizacao = "Por público"
}: TableTitulosReceberProps) {

    const [visualizacao, setVisualizacao] = useState<TOpcoesVisualizacao>(preVisualizacao)

    const opcoesVisualizacao: TOpcoesVisualizacao[] = [
        "Por público",
        "Por função",
        "Por departamento"
    ]

    //query
    const { data: resultTitulosReceber, isLoading: isLoadingTitulosReceber } = useQuery({
        queryKey: ["resumo-titulos-receber", visualizacao],
        queryFn: () => axiosClient.get("financeiro/contas-receber/resumo-titulos-receber", {
            params: {
                visualizacao
            }
        })
    });
    const { dataTitulosReceber }: ITitulosReceber = {
        dataTitulosReceber: resultTitulosReceber?.data || []
    }
    //

    //funções manipuladoras
    const handleChange = (event: SelectChangeEvent) => {
        setVisualizacao(event.target.value as TOpcoesVisualizacao);
    };

    const handleRedirectCell = (dataInicial: Moment | null, dataFinal: Moment, dadoPrimario: string) => {
        const param: any = {
            data_vencimento_inicio: dataInicial,
            data_vencimento_fim: dataFinal
        }

        if (visualizacao === "Por função") param.funcao_pesquisar = dadoPrimario
        if (visualizacao === "Por público") param.tipo_publico_pesquisar = dadoPrimario.charAt(0)
        if (visualizacao === "Por departamento") param.departamento_pesquisar = dadoPrimario
        handleChangePreFilters(param)
    }
    //

    //valores importantes
    const tableHead = useMemo(() => {
        const semPrefix = visualizacao.replace("Por ", '').trim()
        return semPrefix.charAt(0).toUpperCase() + semPrefix.slice(1)
    }, [visualizacao])

    const {
        totalVencidoMais15,
        totalVencidoHa15,
        totalVencidoUltimaSemana,
        totalVenceHoje,
        totalVenceDaqui7,
        totalVenceDaqui15,
        totalVenceMaisQue15
    } = useMemo(() => {
        const totais = {
            totalVencidoMais15: 0,
            totalVencidoHa15: 0,
            totalVencidoUltimaSemana: 0,
            totalVenceHoje: 0,
            totalVenceDaqui7: 0,
            totalVenceDaqui15: 0,
            totalVenceMaisQue15: 0
        };

        dataTitulosReceber.forEach(item => {
            totais.totalVencidoMais15 += parseFloat(item.vencido_mais_15);
            totais.totalVencidoHa15 += parseFloat(item.vencido_ha_15);
            totais.totalVencidoUltimaSemana += parseFloat(item.vencido_ultima_semana);
            totais.totalVenceHoje += parseFloat(item.vence_hoje);
            totais.totalVenceDaqui7 += parseFloat(item.vence_daqui_7);
            totais.totalVenceDaqui15 += parseFloat(item.vence_daqui_15);
            totais.totalVenceMaisQue15 += parseFloat(item.vence_mais_que_15);
        });

        return totais;
    }, [dataTitulosReceber])
    //

    return (
        <Box minWidth={1080}>
            <Box p={2} mb={2} display="flex" alignItems="flex-start" justifyContent="space-between">
                <Typography variant='h6'>Resumo</Typography>

                <FormControl sx={{ width: 180 }}>
                    <InputLabel id="select-visualizacao-receber">Visualização</InputLabel>
                    <Select
                        labelId="select-visualizacao-receber"
                        value={visualizacao}
                        label="Visualização"
                        onChange={handleChange}
                    >
                        {
                            opcoesVisualizacao.map((opcao, index) => (
                                <MenuItem key={index} value={opcao}>{opcao}</MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>
            </Box>
            {
                isLoadingTitulosReceber &&
                <LoadingApiQuery />
            }

            {
                !isLoadingTitulosReceber &&
                <TableContainer>
                    <Table size="small" sx={{ minWidth: 400, overflow: "auto" }}>
                        <TableHead sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 100 }}>
                            <TableRow>
                                <TableCell rowSpan={2} sx={{ fontWeight: 'bold' }}>{tableHead}</TableCell>

                                <TableCell align="right" colSpan={3} sx={{ fontWeight: 'bold', border: 0, bgcolor: coresTabelas.vencidos }}>Vencidos</TableCell>

                                <TableCell align="right" sx={{ fontWeight: 'bold', border: 0 }}>Vence hoje</TableCell>

                                <TableCell align="right" colSpan={3} sx={{ fontWeight: 'bold', border: 0, bgcolor: coresTabelas.paraVencer }}>Para vencer</TableCell>
                            </TableRow>
                            <TableRow>
                                <TableCell align="right" sx={{ bgcolor: coresTabelas.vencidos }}><Typography variant="caption">Mais de 15 dias</Typography></TableCell>
                                <TableCell align="right" sx={{ bgcolor: coresTabelas.vencidos }}><Typography variant="caption">Há 15 dias</Typography></TableCell>
                                <TableCell align="right" sx={{ bgcolor: coresTabelas.vencidos }}><Typography variant="caption">Última semana</Typography></TableCell>

                                <TableCell align="right"><Typography variant="caption">{moment().format("DD/MM/YYYY")}</Typography></TableCell>

                                <TableCell align="right" sx={{ bgcolor: coresTabelas.paraVencer }}><Typography variant="caption">Daqui 7 dias</Typography></TableCell>
                                <TableCell align="right" sx={{ bgcolor: coresTabelas.paraVencer }}><Typography variant="caption">Mais que 15 dias</Typography></TableCell>
                                <TableCell align="right" sx={{ bgcolor: coresTabelas.paraVencer }}><Typography variant="caption">Mais que 30 dias</Typography></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {
                                dataTitulosReceber.map((titulo, index) => {
                                    return (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                cursor: "pointer",
                                                transition: 'background-color 0.2s ease',
                                                "&:hover": {
                                                    backgroundColor: 'rgba(255,244,229,1)'
                                                }
                                            }}
                                        >
                                            <TableCell>
                                                {mostrarDadoPrimario(titulo, visualizacao)}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                onClick={() => {
                                                    handleRedirectCell(
                                                        null,
                                                        moment().subtract(24, 'days'),
                                                        mostrarDadoPrimario(titulo, visualizacao)
                                                    )
                                                }}
                                                sx={{
                                                    bgcolor: coresTabelas.vencidos,
                                                    transition: 'background-color 0.2s ease',
                                                    "&:hover": {
                                                        backgroundColor: 'rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                {formatarValorMonetario(Number(titulo.vencido_mais_15))}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                onClick={() => {
                                                    handleRedirectCell(
                                                        moment().subtract(23, 'days'),
                                                        moment().subtract(23, 'days'),
                                                        mostrarDadoPrimario(titulo, visualizacao)
                                                    )
                                                }}
                                                sx={{
                                                    bgcolor: coresTabelas.vencidos,
                                                    transition: 'background-color 0.2s ease',
                                                    "&:hover": {
                                                        backgroundColor: 'rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                {formatarValorMonetario(Number(titulo.vencido_ha_15))}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                onClick={() => {
                                                    handleRedirectCell(
                                                        moment().subtract(8, 'days'),
                                                        moment().subtract(1, "day"),
                                                        mostrarDadoPrimario(titulo, visualizacao)
                                                    )
                                                }}
                                                sx={{
                                                    bgcolor: coresTabelas.vencidos,
                                                    transition: 'background-color 0.2s ease',
                                                    "&:hover": {
                                                        backgroundColor: 'rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                {formatarValorMonetario(Number(titulo.vencido_ultima_semana))}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                onClick={() => {
                                                    handleRedirectCell(
                                                        moment(),
                                                        moment(),
                                                        mostrarDadoPrimario(titulo, visualizacao)
                                                    )
                                                }}
                                                sx={{
                                                    transition: 'background-color 0.2s ease',
                                                    "&:hover": {
                                                        backgroundColor: 'rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                {formatarValorMonetario(Number(titulo.vence_hoje))}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                onClick={() => {
                                                    handleRedirectCell(
                                                        moment(),
                                                        moment().add(7, 'days'),
                                                        mostrarDadoPrimario(titulo, visualizacao)
                                                    )
                                                }}
                                                sx={{
                                                    bgcolor: coresTabelas.paraVencer,
                                                    transition: 'background-color 0.2s ease',
                                                    "&:hover": {
                                                        backgroundColor: 'rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                {formatarValorMonetario(Number(titulo.vence_daqui_7))}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                onClick={() => {
                                                    handleRedirectCell(
                                                        moment(),
                                                        moment().add(29, 'days'),
                                                        mostrarDadoPrimario(titulo, visualizacao)
                                                    )
                                                }}
                                                sx={{
                                                    bgcolor: coresTabelas.paraVencer,
                                                    transition: 'background-color 0.2s ease',
                                                    "&:hover": {
                                                        backgroundColor: 'rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                {formatarValorMonetario(Number(titulo.vence_daqui_15))}
                                            </TableCell>

                                            <TableCell
                                                align="right"
                                                onClick={() => {
                                                    handleRedirectCell(
                                                        moment(),
                                                        moment().add(90, 'days'),
                                                        mostrarDadoPrimario(titulo, visualizacao)
                                                    )
                                                }}
                                                sx={{
                                                    bgcolor: coresTabelas.paraVencer,
                                                    transition: 'background-color 0.2s ease',
                                                    "&:hover": {
                                                        backgroundColor: 'rgba(0,0,0,0.1)'
                                                    }
                                                }}
                                            >
                                                {formatarValorMonetario(Number(titulo.vence_mais_que_15))}
                                            </TableCell>
                                        </TableRow>
                                    )
                                })
                            }

                            <TableRow>
                                <TableCell />

                                <TableCell align="right" sx={{ fontWeight: "bold", }}>
                                    {formatarValorMonetario(totalVencidoMais15)}
                                </TableCell>

                                <TableCell align="right" sx={{ fontWeight: "bold", }}>
                                    {formatarValorMonetario(totalVencidoHa15)}
                                </TableCell>

                                <TableCell align="right" sx={{ fontWeight: "bold", }}>
                                    {formatarValorMonetario(totalVencidoUltimaSemana)}
                                </TableCell>

                                <TableCell align="right" sx={{ fontWeight: "bold" }}>
                                    {formatarValorMonetario(totalVenceHoje)}
                                </TableCell>

                                <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: coresTabelas.paraVencer }}>
                                    {formatarValorMonetario(totalVenceDaqui7)}
                                </TableCell>

                                <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: coresTabelas.paraVencer }}>
                                    {formatarValorMonetario(totalVenceDaqui15)}
                                </TableCell>

                                <TableCell align="right" sx={{ fontWeight: "bold", bgcolor: coresTabelas.paraVencer }}>
                                    {formatarValorMonetario(totalVenceMaisQue15)}
                                </TableCell>
                            </TableRow>
                        </TableBody>
                    </Table>
                </TableContainer>
            }
        </Box >
    )
}

interface TableTitulosReceberProps {
    handleChangePreFilters: (value: TPreFilters) => void
    preVisualizacao?: TOpcoesVisualizacao,
}

interface ITitulosReceber {
    dataTitulosReceber: IDataResumoTitulosReceber[],
}
